import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Bug, Loader2, Sparkles, Trash2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { Card, CardContent } from '../../components/ui/Card';
import PestModelForm from '../../components/spray/PestModelForm';
import { BLANK_DRAFT, draftFromModel, type PestDraft } from '../../components/spray/pestModelDefaults';
import PestProfileView from '../../components/spray/PestProfileView';
import PestLookupPanel, { type PestLookupFound } from '../../components/spray/PestLookupPanel';
import { PestFarmsPanel, PestAlertRulesPanel } from '../../components/spray/PestManagePanels';
import PestChemicalsPanel from '../../components/spray/PestChemicalsPanel';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import pestModelService from '../../services/pestModels';
import { errorMessage } from '../../utils/apiResponse';
import type { PestModel } from '../../types/pest';

type DetailTab = 'overview' | 'products' | 'edit' | 'farms' | 'alerts';
const TABS: DetailTab[] = ['overview', 'products', 'edit', 'farms', 'alerts'];

function draftFromProposal(p: PestLookupFound, existing?: PestModel | null): PestDraft {
  return {
    // Re-researching an existing pest keeps the name the farm already uses and
    // any hosts somebody added; everything descriptive comes from the research.
    name: existing?.name ?? p.name,
    common_name: p.common_name ?? existing?.common_name ?? null,
    scientific_name: p.scientific_name ?? existing?.scientific_name ?? null,
    kind: p.kind,
    description: p.description,
    model_type: p.model_type,
    model_params: p.model_params,
    recommended_actions: p.recommended_actions,
    host_crop_type_ids: [...new Set([...(existing?.host_crop_type_ids ?? []), ...p.host_crop_type_ids])],
    is_active: existing?.is_active ?? true,
    profile: p.profile,
  };
}

/**
 * /spray/pests/new and /spray/pests/:id.
 *
 * New pests start with research: the grower says what they are looking at, the
 * server identifies and researches it, and the proposal lands in the form for
 * review. Nothing is written until Create is pressed.
 */
export default function PestDiseaseDetail() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManage = useModuleLicense('spray', 'admin').hasAccess;

  const [model, setModel] = useState<PestModel | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<PestDraft | null>(null);
  const [draftKey, setDraftKey] = useState(0);
  const [review, setReview] = useState<PestLookupFound | null>(null);
  const [researching, setResearching] = useState(false);

  const tabParam = searchParams.get('tab') as DetailTab | null;
  const tab: DetailTab = tabParam && TABS.includes(tabParam) ? tabParam : 'overview';
  const setTab = (t: DetailTab) => setSearchParams(t === 'overview' ? {} : { tab: t }, { replace: true });

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try { setModel(await pestModelService.getModel(Number(id))); setError(null); }
    catch (e) { setError(errorMessage(e)); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const startDraft = (d: PestDraft, proposal: PestLookupFound | null) => {
    setDraft(d);
    setReview(proposal);
    setDraftKey((k) => k + 1);
    setResearching(false);
  };

  const onFound = (p: PestLookupFound) => {
    startDraft(draftFromProposal(p, model), p);
    if (model) setTab('edit');
  };

  const onSaved = (saved: PestModel) => {
    if (isNew) { navigate(`/spray/pests/${saved.id}`, { replace: true }); return; }
    setModel(saved);
    setDraft(null);
    setReview(null);
    setTab('overview');
  };

  const discardDraft = () => { setDraft(null); setReview(null); setDraftKey((k) => k + 1); };

  const remove = async () => {
    if (!model || !confirm(`Delete "${model.name}"? This removes its risk history and alert rules.`)) return;
    try { await pestModelService.deleteModel(model.id); navigate('/spray/pests', { replace: true }); }
    catch (e) { setError(errorMessage(e)); }
  };

  const headerActions = model && canManage ? (
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" size="sm" onClick={() => { setResearching(true); setTab('overview'); }} disabled={researching}>
        <Sparkles className="h-4 w-4 text-primary-600" /> Research with AI
      </Button>
      <Button variant="outline" size="sm" onClick={remove} aria-label="Delete pest"><Trash2 className="h-4 w-4 text-red-600" /> <span className="hidden sm:inline">Delete</span></Button>
    </div>
  ) : undefined;

  return (
    <>
      <PageHeader
        title={isNew ? (draft?.name || 'New pest or disease') : (model?.name ?? 'Pest or disease')}
        subtitle={isNew ? 'Identify it, research it, set up its forecast' : (model?.scientific_name || undefined)}
        icon={<Bug className="h-6 w-6 text-white" />}
        actionButtons={headerActions}
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2 text-sm">{error}</div>}

        {isNew ? (
          !canManage ? (
            <Card><CardContent className="py-8 text-center text-neutral-500">A spray admin licence seat is needed to add pests.</CardContent></Card>
          ) : !draft ? (
            <PestLookupPanel
              onFound={onFound}
              onManual={() => startDraft(BLANK_DRAFT, null)}
              onCancel={() => navigate('/spray/pests')}
            />
          ) : (
            <>
              {review && <ReviewBanner proposal={review} onStartOver={discardDraft} />}
              <PestModelForm key={draftKey} model={null} draft={draft} canManage={canManage} onSaved={onSaved} onCancel={discardDraft} />
              {draft.profile && (
                <div>
                  <h2 className="text-sm font-semibold text-neutral-700 mb-2">Field guide preview — saved with the pest</h2>
                  <PestProfileView pest={previewModel(draft)} />
                </div>
              )}
            </>
          )
        ) : loading ? (
          <div className="flex items-center gap-2 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
        ) : !model ? null : (
          <>
            <Tabs<DetailTab>
              tabs={[
                { key: 'overview', label: 'Field guide' },
                { key: 'products', label: 'Products' },
                { key: 'edit', label: 'Forecast & definition' },
                { key: 'farms', label: 'Farms' },
                { key: 'alerts', label: 'Alerts' },
              ]}
              active={tab}
              onChange={setTab}
            />

            {researching && (
              <PestLookupPanel
                title={`Research ${model.name}`}
                initialQuery={model.scientific_name || model.name}
                onFound={onFound}
                onCancel={() => setResearching(false)}
              />
            )}

            {tab === 'overview' && !researching && (
              <PestProfileView pest={model} onResearch={canManage ?() => setResearching(true) : undefined} />
            )}
            {tab === 'edit' && (
              <>
                {review && <ReviewBanner proposal={review} onStartOver={discardDraft} existing />}
                <PestModelForm
                  key={`${model.id}-${draftKey}`}
                  model={model}
                  draft={draft ?? draftFromModel(model)}
                  canManage={canManage}
                  onSaved={onSaved}
                  onCancel={draft ? discardDraft : undefined}
                />
              </>
            )}
            {tab === 'products' && <Card><CardContent className="pt-5"><PestChemicalsPanel pest={model} canManage={canManage} /></CardContent></Card>}
            {tab === 'farms' && <Card><CardContent className="pt-5"><PestFarmsPanel pest={model} canManage={canManage} /></CardContent></Card>}
            {tab === 'alerts' && <Card><CardContent className="pt-5"><PestAlertRulesPanel pest={model} canManage={canManage} /></CardContent></Card>}
          </>
        )}
      </div>
    </>
  );
}

/** A draft dressed as a record, so the field guide can be previewed before saving. */
function previewModel(d: PestDraft): PestModel {
  return {
    id: 0, organisation_id: null, name: d.name, common_name: d.common_name ?? null, scientific_name: d.scientific_name ?? null,
    kind: d.kind, description: d.description ?? null, model_type: d.model_type, model_params: d.model_params,
    recommended_actions: d.recommended_actions ?? null, profile: d.profile ?? null,
    is_active: d.is_active ?? true,
    host_crop_type_ids: d.host_crop_type_ids, created_at: '', updated_at: '',
  };
}

const CONFIDENCE_CLS: Record<string, string> = {
  high: 'bg-emerald-100 text-emerald-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-red-100 text-red-800',
};

function ReviewBanner({ proposal, onStartOver, existing }: { proposal: PestLookupFound; onStartOver: () => void; existing?: boolean }) {
  return (
    <div className="rounded-xl border border-primary-200 bg-primary-50/60 px-4 py-3 space-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary-600" />
        <span className="font-semibold text-neutral-900">Researched — review before saving</span>
        <span className={`text-[11px] px-1.5 py-0.5 rounded ${CONFIDENCE_CLS[proposal.confidence]}`}>{proposal.confidence} confidence</span>
        <button onClick={onStartOver} className="ml-auto text-xs text-primary-700 hover:underline">{existing ? 'Discard' : 'Start over'}</button>
      </div>
      <p className="text-neutral-600">
        {existing
          ? 'The research has been loaded into the form below, replacing the forecast model and description. Your pest name and hosts are kept. Nothing changes until you save.'
          : 'Everything below was filled in from the research. Check the forecast parameters and hosts in particular — they drive the risk alerts. Nothing is saved until you press Create pest.'}
      </p>
      {proposal.existing_match && !existing && (
        <p className="text-amber-800 flex items-start gap-1.5">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            You already have <Link to={`/spray/pests/${proposal.existing_match.id}`} className="font-medium underline">{proposal.existing_match.name}</Link>
            {' — open it instead of creating a duplicate.'}
          </span>
        </p>
      )}
      {proposal.warnings.length > 0 && (
        <ul className="text-amber-900 space-y-1">
          {proposal.warnings.map((w, i) => <li key={i} className="flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />{w}</li>)}
        </ul>
      )}
      {proposal.hosts_not_listed.length > 0 && (
        <p className="text-xs text-neutral-500">
          Also attacks crops not in your crop list: {proposal.hosts_not_listed.join(', ')}. Add them under Cultivar admin → Crop types if you grow them.
        </p>
      )}
    </div>
  );
}
